// src/pages/RegisterPage.js — Premium register page
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth }  from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Button, Input } from '../components/ui';
import { getFieldErrors } from '../services/api';

export default function RegisterPage() {
  const navigate     = useNavigate();
  const { register } = useAuth();
  const { toast }    = useToast();

  const [form,setForm] = useState({ name:'', email:'', phone:'', password:'', password2:'' });
  const [errors, setErrors] = useState({});
  const [loading,setLoading] = useState(false);

  const set = f => e => { setForm(p=>({...p,[f]:e.target.value})); setErrors(p=>({...p,[f]:''})); };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = 'Enter your full name (min 2 chars).';
    if (!form.email.trim()) errs.email = 'Email is required.';
    if (form.password.length < 8) errs.password = 'Password must be at least 8 characters.';
    if (form.password !== form.password2) errs.password2 = 'Passwords do not match.';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Welcome aboard, ${user.name.split(' ')[0]}! 🎉`);
      navigate('/', { replace:true });
    } catch (err) {
      const fieldErrs = getFieldErrors(err);
      if (Object.keys(fieldErrs).length) setErrors(fieldErrs);
      else toast.error(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{background:'#fffaf0'}}>
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-red-950 via-red-800 to-red-700
        relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 deco-stripes opacity-30"/>
        {[...Array(12)].map((_,i) => (
          <span key={i} className="absolute rounded-full animate-ping pointer-events-none" style={{
            width:`${2+(i%3)*3}px`, height:`${2+(i%3)*3}px`,
            top:`${10+(i*19)%80}%`, left:`${5+(i*23)%90}%`,
            backgroundColor:['#fbbf24','#fde68a','#ef4444'][i%3],
            animationDuration:`${1.5+(i%3)*0.8}s`, animationDelay:`${i*0.3}s`, opacity:0.5,
          }}/>
        ))}
        <div className="relative text-center px-12 animate-fade-in">
          <div className="text-8xl mb-6" style={{filter:'drop-shadow(0 0 40px rgba(245,200,66,0.5))'}}>🎆</div>
          <h2 className="text-white font-black text-4xl mb-4" style={{fontFamily:'"Playfair Display",serif'}}>
            Join Lakshmi Crackers
          </h2>
          <p className="text-red-200/70 text-base leading-relaxed max-w-xs mx-auto">
            Create your free account and unlock exclusive Diwali deals, order tracking, and early access.
          </p>
          <div className="mt-10 space-y-3">
            {['✓ Track all your orders', '✓ Exclusive member deals', '✓ Priority Diwali delivery'].map(t => (
              <div key={t} className="flex items-center gap-3 text-yellow-300/80 text-sm font-medium">
                <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full flex-shrink-0"/>{t}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <img src="/logo.png" alt="Lakshmi Crackers" className="w-12 h-12 object-contain" style={{filter:"drop-shadow(0 0 8px rgba(245,200,66,0.6))"}}/>
              <span className="font-black text-red-700 text-xl" style={{fontFamily:'"Playfair Display",serif'}}>
                Lakshmi Crackers
              </span>
            </Link>
          </div>
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-900 mb-1" style={{fontFamily:'"Playfair Display",serif'}}>
              Create Account
            </h1>
            <p className="text-gray-500 text-sm">
              Already have one?{' '}
              <Link to="/login" className="text-red-600 font-bold hover:underline">Sign in →</Link>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input label="Full Name" value={form.name} onChange={set('name')} error={errors.name} required placeholder="Priya Ramesh"/>
            <Input label="Email Address" type="email" value={form.email} onChange={set('email')} error={errors.email} required placeholder="you@example.com" autoComplete="email"/>
            <Input label="Phone Number" value={form.phone} onChange={set('phone')} error={errors.phone} placeholder="10-digit mobile (optional)"/>
            <Input label="Password" type="password" value={form.password} onChange={set('password')} error={errors.password} required
              placeholder="Min 8 chars, 1 uppercase, 1 digit"
              hint="At least 8 characters, one uppercase letter and one digit."/>
            <Input label="Confirm Password" type="password" value={form.password2} onChange={set('password2')} error={errors.password2} required placeholder="Repeat your password"/>
            <Button type="submit" loading={loading} size="lg" className="w-full mt-2"
              style={{background:'linear-gradient(135deg, #c0152a, #8b0e1d)'}}>
              {loading ? 'Creating Account…' : '🎉 Create Account'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
